import React, { createContext, useContext, useState, useEffect } from 'react'
import api from '../services/api'

interface User {
  user_id: string
  email: string
  given_name: string
  surname: string
  display_name: string
  customer_id: string
  roles: string[]
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

interface RegisterData {
  email: string
  password: string
  given_name: string
  surname: string
  company_name?: string
  job_title?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = async () => {
    try {
      const userData = await api.auth.me()
      setUser(userData)
    } catch (error) {
      console.error('Failed to refresh user:', error)
      throw error
    }
  }

  // Check for existing token and load user on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token')
      if (token) {
        api.setToken(token)
        try {
          await refreshUser()
        } catch (error) {
          // Token invalid, clear it
          localStorage.removeItem('auth_token')
          api.setToken(null)
        }
      }
      setIsLoading(false)
    }
    initAuth()
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const response = await api.auth.login({ email, password })
      const { token, user: userData } = response
      
      // Store token
      localStorage.setItem('auth_token', token)
      api.setToken(token)
      
      setUser(userData)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const register = async (data: RegisterData) => {
    try {
      const response = await api.auth.register({
        email: data.email,
        password: data.password,
        given_name: data.given_name,
        surname: data.surname,
        company_name: data.company_name,
        job_title: data.job_title
      })
      
      if (!response || !response.token) {
        throw new Error('Invalid registration response')
      }
      
      const { token, user: userData } = response
      
      console.log('[AuthContext] Registration successful, setting token')
      
      // Store token
      localStorage.setItem('auth_token', token)
      api.setToken(token)
      
      // Verify token was set
      console.log('[AuthContext] Token set:', api.getToken()?.substring(0, 20) + '...')
      
      setUser(userData)
    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await api.auth.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      localStorage.removeItem('auth_token')
      api.setToken(null)
      setUser(null)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Export for backwards compatibility
export { AuthProvider as B2CAuthProvider, useAuth as useB2CAuth }
